import {
  collection,
  addDoc,
  updateDoc,
  doc,
  query,
  where,
  getDocs,
  getDoc,
  orderBy,
  Timestamp,
  setDoc,
  increment,
  deleteDoc,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from './firebase';

const BOOKINGS_COLLECTION = 'bookings';

export const createBooking = async (bookingData) => {
  const docRef = await addDoc(collection(db, BOOKINGS_COLLECTION), {
    status: 'pending',
    termsAccepted: false,
    termsAcceptedAt: null,
    ...bookingData,
    createdAt: Timestamp.now(),
  });
  return docRef.id;
};

export const getBookingById = async (bookingId) => {
  const snap = await getDoc(doc(db, BOOKINGS_COLLECTION, bookingId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
};

export const getUserBookings = async (userId) => {
  const q = query(
    collection(db, BOOKINGS_COLLECTION),
    where('userId', '==', userId)
  );
  const snap = await getDocs(q);
  const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  // Sort client-side to avoid needing a composite Firestore index
  return docs.sort((a, b) => {
    const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
    const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
    return bTime - aTime;
  });
};

export const getBookingsForSpace = async (spaceId, startDate, endDate) => {
  // Single-field query only (no composite index required); filter client-side
  const q = query(
    collection(db, BOOKINGS_COLLECTION),
    where('spaceId', '==', spaceId)
  );
  const snap = await getDocs(q);
  const bookings = snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((b) => {
      if (b.status === 'cancelled') return false;
      const bStart = b.startDate?.toDate ? b.startDate.toDate() : new Date(b.startDate);
      const bEnd = b.endDate?.toDate ? b.endDate.toDate() : new Date(b.endDate);
      // Keep if booking overlaps the queried range
      return bStart <= endDate && bEnd >= startDate;
    });

  // Also include manual blocks for this space (client-side overlap filter)
  const manualBlocks = await getManualBlocksForSpace(spaceId);
  const blockEvents = manualBlocks
    .filter((b) => {
      const bStart = b.startDate?.toDate ? b.startDate.toDate() : new Date(b.startDate);
      const bEnd = b.endDate?.toDate ? b.endDate.toDate() : new Date(b.endDate);
      return bStart <= endDate && bEnd >= startDate;
    })
    .map((b) => ({
      id: b.id,
      spaceId: b.spaceId,
      startDate: Timestamp.fromDate(b.startDate?.toDate ? b.startDate.toDate() : new Date(b.startDate)),
      endDate: Timestamp.fromDate(b.endDate?.toDate ? b.endDate.toDate() : new Date(b.endDate)),
      status: 'confirmed',
      isManualBlock: true,
      seatName: b.seatName || null,
      note: b.note,
      memberName: b.memberName,
    }));

  return [...bookings, ...blockEvents];
};

const getManualBlocksForSpace = async (spaceId) => {
  try {
    const q = query(collection(db, 'manualBlocks'), where('spaceId', '==', spaceId));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error('[Firestore] getManualBlocksForSpace failed:', err);
    try {
      const all = JSON.parse(localStorage.getItem('hub_manual_blocks') ?? '[]');
      return all.filter((b) => b.spaceId === spaceId);
    } catch {
      return [];
    }
  }
};

export const getAllConfirmedBookings = async (startDate, endDate) => {
  // No composite index needed — filter client-side
  const snap = await getDocs(collection(db, BOOKINGS_COLLECTION));
  const bookings = snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((b) => {
      if (b.status === 'cancelled') return false;
      const bStart = b.startDate?.toDate ? b.startDate.toDate() : new Date(b.startDate);
      const bEnd = b.endDate?.toDate ? b.endDate.toDate() : new Date(b.endDate);
      return bStart <= endDate && bEnd >= startDate;
    });

  // Include all manual blocks
  try {
    const blocksSnap = await getDocs(collection(db, 'manualBlocks'));
    const blocks = blocksSnap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((b) => new Date(b.startDate) <= endDate && new Date(b.endDate) >= startDate)
      .map((b) => ({
        id: b.id,
        spaceId: b.spaceId,
        startDate: Timestamp.fromDate(new Date(b.startDate)),
        endDate: Timestamp.fromDate(new Date(b.endDate)),
        status: 'confirmed',
        isManualBlock: true,
        title: b.memberName ? `Manual: ${b.memberName}` : 'Manual Block',
      }));
    return [...bookings, ...blocks];
  } catch {
    return bookings;
  }
};

export const updateBookingStatus = async (bookingId, status) => {
  await updateDoc(doc(db, BOOKINGS_COLLECTION, bookingId), { status });
};

export const markTermsAccepted = async (bookingId, signatureDataUrl) => {
  await updateDoc(doc(db, BOOKINGS_COLLECTION, bookingId), {
    termsAccepted: true,
    termsAcceptedAt: Timestamp.now(),
    ...(signatureDataUrl ? { signature: signatureDataUrl, signedAt: Timestamp.now() } : {}),
  });
};

export const cancelBooking = async (bookingId) => {
  const bookingRef = doc(db, BOOKINGS_COLLECTION, bookingId);
  const snap = await getDoc(bookingRef);
  const booking = snap.exists() ? snap.data() : null;

  await updateDoc(bookingRef, { status: 'cancelled' });

  // Reverse any meeting room credits granted by this booking
  if (booking?.creditsGranted > 0 && booking?.userId && booking?.creditMonth) {
    try {
      const creditRef = doc(db, 'memberCredits', booking.userId);
      const creditSnap = await getDoc(creditRef);
      if (creditSnap.exists()) {
        const data = creditSnap.data();
        const month = booking.creditMonth;
        const existing = data.credits?.[month];
        if (existing) {
          const newTotal = Math.max(existing.used, existing.total - booking.creditsGranted);
          await updateDoc(creditRef, { [`credits.${month}.total`]: newTotal });
        }
      }
    } catch (err) {
      console.error('[Credits] failed to reverse credits on cancellation:', err);
    }
  }

  // Cancel all guest bookings linked to this booking
  const guestQ = query(
    collection(db, BOOKINGS_COLLECTION),
    where('parentBookingId', '==', bookingId),
    where('type', '==', 'guest'),
  );
  const guestSnap = await getDocs(guestQ);
  for (const g of guestSnap.docs) {
    if (g.data().status !== 'cancelled') {
      await updateDoc(g.ref, { status: 'cancelled', accessCodeStatus: 'revoked' });
    }
  }
};

export const getAllBookings = async () => {
  const q = query(collection(db, BOOKINGS_COLLECTION), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const updateBookingPaymentMethod = async (bookingId, paymentMethod) => {
  await updateDoc(doc(db, BOOKINGS_COLLECTION, bookingId), { paymentMethod });
};

export const markPaymentComplete = async (bookingId, stripePaymentIntentId) => {
  await updateDoc(doc(db, BOOKINGS_COLLECTION, bookingId), {
    paymentStatus: 'paid',
    status: 'confirmed',
    ...(stripePaymentIntentId ? { stripePaymentIntentId } : {}),
  });
};

export const updatePaymentSchedule = async (bookingId, paymentSchedule) => {
  await updateDoc(doc(db, BOOKINGS_COLLECTION, bookingId), { paymentSchedule });
};

export const markRefunded = async (bookingId, refundId) => {
  await updateDoc(doc(db, BOOKINGS_COLLECTION, bookingId), {
    refundStatus: 'refunded',
    refundId,
    refundedAt: new Date().toISOString(),
  });
};

export const addCreditNoteRef = async (bookingId, creditNoteRef) => {
  await updateDoc(doc(db, BOOKINGS_COLLECTION, bookingId), {
    refundStatus: 'credit_note',
    creditNoteRef,
  });
};

export const getManualBlocksForUser = async (email) => {
  const q = query(collection(db, 'manualBlocks'), where('userEmail', '==', email));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data(), _isManualBlock: true }));
};

export const getAllUsers = async () => {
  const snap = await getDocs(collection(db, 'users'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const updateUserWifiProfile = async (uid, wifiProfileId) => {
  await updateDoc(doc(db, 'users', uid), { wifiProfileId: wifiProfileId || null });
};

export const updateUserProfile = async (uid, data) => {
  await updateDoc(doc(db, 'users', uid), data);
};

export const updateUserRole = async (uid, role) => {
  const functions = getFunctions(undefined, 'australia-southeast1');
  const fn = httpsCallable(functions, 'updateUserRole');
  await fn({ uid, role }); // validates caller is admin server-side
  await updateDoc(doc(db, 'users', uid), { role }); // write performed client-side
};

// Legacy — kept for migration
export const getSubMembers = async (uid) => {
  try {
    const snap = await getDoc(doc(db, 'users', uid));
    return snap.exists() ? (snap.data().subMembers || []) : [];
  } catch { return []; }
};

export const saveSubMembers = async (uid, subMembers) => {
  await updateDoc(doc(db, 'users', uid), { subMembers });
};

// ─── Team Roster (user-level) ─────────────────────────────────────────────────

export const getTeamRoster = async (uid) => {
  const snap = await getDocs(collection(db, 'users', uid, 'teamMembers'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const addTeamRosterMember = async (uid, member) => {
  const ref = await addDoc(collection(db, 'users', uid, 'teamMembers'), {
    ...member,
    createdAt: Timestamp.now(),
  });
  return ref.id;
};

export const removeTeamRosterMember = async (uid, memberId) => {
  const ref = doc(db, 'users', uid, 'teamMembers', memberId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  // Also cancel any active guest bookings for this team member
  const q = query(
    collection(db, BOOKINGS_COLLECTION),
    where('teamMemberId', '==', memberId),
    where('type', '==', 'guest'),
  );
  const guestSnap = await getDocs(q);
  for (const g of guestSnap.docs) {
    if (g.data().status !== 'cancelled') {
      await updateDoc(g.ref, { status: 'cancelled', accessCodeStatus: 'revoked' });
    }
  }
  await deleteDoc(ref);
};

// ─── Guest Access Bookings ────────────────────────────────────────────────────

export const createGuestBooking = async ({ parentBookingId, parentUserId, teamMemberId, firstName, lastName, email, phone, accessPin, spaceId, startDate, endDate }) => {
  const ref = await addDoc(collection(db, BOOKINGS_COLLECTION), {
    type: 'guest',
    parentBookingId,
    parentUserId,
    ...(teamMemberId ? { teamMemberId } : {}),
    firstName,
    lastName: lastName || '',
    email: email || '',
    phone: phone || '',
    accessPin,
    accessCodeStatus: 'pending',
    spaceId,
    startDate,
    endDate,
    status: 'confirmed',
    createdAt: Timestamp.now(),
  });
  return ref.id;
};

export const getGuestBookings = async (parentBookingId) => {
  const q = query(
    collection(db, BOOKINGS_COLLECTION),
    where('parentBookingId', '==', parentBookingId),
    where('type', '==', 'guest'),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const revokeGuestBooking = async (guestBookingId) => {
  await updateDoc(doc(db, BOOKINGS_COLLECTION, guestBookingId), {
    status: 'cancelled',
    accessCodeStatus: 'revoked',
  });
};

// ─── Meeting Room Credits ─────────────────────────────────────────────────────

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const hasActiveMonthlyMembership = async (userId) => {
  try {
    const q = query(collection(db, BOOKINGS_COLLECTION), where('userId', '==', userId));
    const snap = await getDocs(q);
    const now = new Date();
    return snap.docs.some((d) => {
      const b = d.data();
      if (b.pricingType !== 'monthly' || b.status === 'cancelled') return false;
      const end = b.endDate?.toDate ? b.endDate.toDate() : new Date(b.endDate);
      return end >= now;
    });
  } catch { return false; }
};

/**
 * Get the current month's meeting room credit balance for a user.
 * Auto-provisions 20 hrs if user has an active monthly membership and this month isn't provisioned yet.
 */
export const getMemberCreditBalance = async (userId) => {
  const month = currentMonth();
  try {
    const ref = doc(db, 'memberCredits', userId);
    const snap = await getDoc(ref);
    const data = snap.exists() ? snap.data() : {};
    let monthCredits = data.credits?.[month] || null;

    if (!monthCredits) {
      const hasMonthly = await hasActiveMonthlyMembership(userId);
      if (hasMonthly) {
        monthCredits = { total: 20, used: 0 };
        await setDoc(ref, {
          email: data.email || '',
          name: data.name || '',
          credits: { ...(data.credits || {}), [month]: monthCredits },
        }, { merge: true });
      } else {
        return { month, total: 0, used: 0, available: 0 };
      }
    }

    return {
      month,
      total: monthCredits.total,
      used: monthCredits.used,
      available: Math.max(0, monthCredits.total - monthCredits.used),
    };
  } catch (err) {
    console.error('[Credits] getMemberCreditBalance failed:', err);
    return { month, total: 0, used: 0, available: 0 };
  }
};

/**
 * Provision 20 hrs of meeting room credits for the current month when a monthly membership is purchased.
 */
export const provisionMonthlyCredits = async (userId, email, name, bookingId) => {
  const month = currentMonth();
  const ref = doc(db, 'memberCredits', userId);
  try {
    const snap = await getDoc(ref);
    const data = snap.exists() ? snap.data() : {};
    if (!data.credits?.[month]) {
      await setDoc(ref, {
        email: email || data.email || '',
        name: name || data.name || '',
        credits: { ...(data.credits || {}), [month]: { total: 20, used: 0 } },
      }, { merge: true });
      // Record on the booking so cancellation can reverse credits
      if (bookingId) {
        await updateDoc(doc(db, BOOKINGS_COLLECTION, bookingId), {
          creditsGranted: 20,
          creditMonth: month,
        });
      }
    }
  } catch (err) {
    console.error('[Credits] provisionMonthlyCredits failed:', err);
  }
};

/**
 * Add meeting room credits for a day pass purchase.
 * Credits are additive — each day pass stacks on existing balance for the month.
 */
export const provisionDailyPassCredits = async (userId, email, name, hours, bookingId) => {
  if (!hours || hours <= 0) return;
  const month = currentMonth();
  const ref = doc(db, 'memberCredits', userId);
  try {
    const snap = await getDoc(ref);
    const data = snap.exists() ? snap.data() : {};
    const existing = data.credits?.[month] || { total: 0, used: 0 };
    await setDoc(ref, {
      email: email || data.email || '',
      name: name || data.name || '',
      credits: { ...(data.credits || {}), [month]: { total: existing.total + hours, used: existing.used } },
    }, { merge: true });
    // Record on the booking so cancellation can reverse credits
    if (bookingId) {
      await updateDoc(doc(db, BOOKINGS_COLLECTION, bookingId), {
        creditsGranted: hours,
        creditMonth: month,
      });
    }
  } catch (err) {
    console.error('[Credits] provisionDailyPassCredits failed:', err);
  }
};

/** Deduct credit hours after a meeting room booking is paid. */
export const deductMeetingRoomCredits = async (userId, hoursUsed, month) => {
  try {
    await updateDoc(doc(db, 'memberCredits', userId), {
      [`credits.${month}.used`]: increment(hoursUsed),
    });
  } catch (err) {
    console.error('[Credits] deductMeetingRoomCredits failed:', err);
  }
};

/** Admin: add hours to a member's credit allowance for a given month. */
export const topUpMemberCredits = async (userId, month, additionalHours) => {
  const ref = doc(db, 'memberCredits', userId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Member credit record not found');
  const data = snap.data();
  const existing = data.credits?.[month] || { total: 0, used: 0 };
  await setDoc(ref, {
    credits: { ...(data.credits || {}), [month]: { ...existing, total: existing.total + additionalHours } },
  }, { merge: true });
};

/** Admin: load all member credit records. */
export const getAllMemberCredits = async () => {
  try {
    const snap = await getDocs(collection(db, 'memberCredits'));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error('[Credits] getAllMemberCredits failed:', err);
    return [];
  }
};

// Generate mock availability events for the calendar (used when Firebase not configured)
export const getMockBookings = (startDate, endDate) => {
  const events = [];
  const spaces = ['hot-desk', 'dedicated-desk', 'meeting-room'];
  const now = new Date(startDate);
  while (now <= endDate) {
    const day = now.getDay();
    if (day !== 0 && day !== 6) {
      // Add a few random bookings per day
      if (Math.random() > 0.5) {
        const hour = 9 + Math.floor(Math.random() * 4);
        const start = new Date(now);
        start.setHours(hour, 0, 0, 0);
        const end = new Date(start);
        end.setHours(hour + 2, 0, 0, 0);
        events.push({
          id: `mock-${spaces[1]}-${now.toDateString()}`,
          spaceId: 'meeting-room',
          title: 'Booked',
          startDate: start,
          endDate: end,
          status: 'confirmed',
        });
      }
    }
    now.setDate(now.getDate() + 1);
  }
  return events;
};
